import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useSetRecoilState } from 'recoil';
import errorOrYesModalState from '../../recoil/errorOrYesModal/atom';
import { imgTypes } from '@dataTypes/imgTypes';
import { useNotificationManagement } from '@hooks/notification/useNotificationManagement';
import NotificationModal from '@components/common/NotificationModal';
import NoticeRichTextEditor from '@components/notification/NoticeRichTextEditor';
import {
  isEmptyNotice,
  looksLikeHtml,
  sanitizeNoticeHtml,
} from '@components/notification/noticeHtml';
import { uploadNoticeImage } from '@apis/notification';
import { INoticeItem, INoticeWriter } from '@dataTypes/notificationTypes';
import axios from 'axios';

type NoticeTab = 'list' | 'grant';

const NotificationManagementContainer = () => {
  const setErrorState = useSetRecoilState(errorOrYesModalState);
  const [tab, setTab] = useState<NoticeTab>('list');
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [showPopup, setShowPopup] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [preview, setPreview] = useState(false);
  const [grantId, setGrantId] = useState('');
  const [formKey, setFormKey] = useState(0);

  const {
    access,
    notices,
    noticesLoading,
    writers,
    saveNoticeAsync,
    saveLoading,
    updateNoticeAsync,
    updateLoading,
    deleteNoticeAsync,
    deleteLoading,
    grantAsync,
    grantLoading,
    revokeAsync,
    revokeLoading,
    updatePermissionsAsync,
    permissionLoading,
  } = useNotificationManagement();

  const notify = (modalTitle: string, text: string) => {
    setErrorState({
      status: true,
      imgType: imgTypes.ERROR,
      title: modalTitle,
      text,
      modalBG: true,
    });
  };

  const errorMessage = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error)) {
      return error.response?.data?.message || fallback;
    }
    return fallback;
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setShowPopup(true);
    setEditingId(null);
    setShowForm(false);
  };

  const openCreateForm = () => {
    setTitle('');
    setContent('');
    setShowPopup(true);
    setEditingId(null);
    setFormKey((key) => key + 1);
    setShowForm(true);
  };

  const selectNotice = (notice: INoticeItem) => {
    setEditingId(notice.popupId);
    setTitle(notice.title);
    setContent(notice.content || '');
    setShowPopup(!!notice.showPopup);
    setFormKey((key) => key + 1);
    setShowForm(true);
  };

  const contentForSave = () => (looksLikeHtml(content) ? sanitizeNoticeHtml(content) : content);

  const validate = (safeContent: string) => {
    if (title.trim() === '' || isEmptyNotice(safeContent)) {
      notify('입력창을 다시 확인해주세요.', '공지사항 제목과 내용을 입력해주세요.');
      return false;
    }
    if (!looksLikeHtml(safeContent)) {
      const urls = safeContent.match(/(https?:\/\/[^\s]+)/g) ?? [];
      const invalidUrls = urls.filter((url) => {
        try {
          const parsed = new URL(url);
          return parsed.protocol !== 'http:' && parsed.protocol !== 'https:';
        } catch {
          return true;
        }
      });
      if (invalidUrls.length > 0) {
        notify('유효하지 않은 링크가 포함되어 있습니다.', invalidUrls.join('\n'));
        return false;
      }
    }
    return true;
  };

  const saveNotificationBtn = async () => {
    const safeContent = contentForSave();
    if (!validate(safeContent)) return;
    const payload = { title: title.trim(), content: safeContent, showPopup };
    try {
      const result =
        editingId == null
          ? await saveNoticeAsync(payload)
          : await updateNoticeAsync({ data: payload, popupId: editingId });
      if (result.success) {
        notify('공지사항 저장 완료', '공지사항이 저장되었습니다.');
        resetForm();
      } else {
        notify('공지사항 저장 실패', '공지사항 저장에 실패했습니다.');
      }
    } catch (error) {
      notify('공지사항 저장 실패', errorMessage(error, '공지사항 저장에 실패했습니다.'));
    }
  };

  const confirmDeleteBtn = async (popupId: number) => {
    try {
      const result = await deleteNoticeAsync(popupId);
      if (result.success) {
        if (editingId === popupId) resetForm();
        notify('공지사항 삭제 완료', '공지사항이 삭제되었습니다.');
      } else {
        notify('공지사항 삭제 실패', '공지사항 삭제에 실패했습니다.');
      }
    } catch (error) {
      notify('공지사항 삭제 실패', errorMessage(error, '공지사항 삭제에 실패했습니다.'));
    }
  };

  const grantBtn = async () => {
    if (grantId.trim() === '') {
      notify('아이디를 입력해주세요.', '작성 권한을 줄 로그인 아이디를 입력해주세요.');
      return;
    }
    try {
      await grantAsync(grantId.trim());
      setGrantId('');
      notify('권한 부여 완료', '해당 아이디로 공지사항을 작성할 수 있습니다.');
    } catch (error) {
      notify('권한 부여 실패', errorMessage(error, '권한 부여에 실패했습니다.'));
    }
  };

  const changePermission = async (
    writer: INoticeWriter,
    key: 'canCreate' | 'canUpdate' | 'canDelete',
    value: boolean,
  ) => {
    try {
      await updatePermissionsAsync({
        userId: writer.userId,
        data: {
          canCreate: key === 'canCreate' ? value : !!writer.canCreate,
          canUpdate: key === 'canUpdate' ? value : !!writer.canUpdate,
          canDelete: key === 'canDelete' ? value : !!writer.canDelete,
        },
      });
    } catch (error) {
      notify('권한 수정 실패', errorMessage(error, '권한 수정에 실패했습니다.'));
    }
  };

  const revokeWriter = async (writer: INoticeWriter) => {
    try {
      const result = await revokeAsync(writer.userId);
      if (result.success) {
        notify('권한 해제 완료', `${writer.loginId} 작성 권한을 해제했습니다.`);
      } else {
        notify('권한 해제 실패', '작성 권한 해제에 실패했습니다.');
      }
    } catch (error) {
      notify('권한 해제 실패', errorMessage(error, '작성 권한 해제에 실패했습니다.'));
    }
  };

  const permissionCell = (writer: INoticeWriter) => (
    <span className="perm_checks">
      <label>
        <input
          type="checkbox"
          checked={!!writer.canCreate}
          disabled={permissionLoading}
          onChange={(e) => changePermission(writer, 'canCreate', e.target.checked)}
        />
        등록
      </label>
      <label>
        <input
          type="checkbox"
          checked={!!writer.canUpdate}
          disabled={permissionLoading}
          onChange={(e) => changePermission(writer, 'canUpdate', e.target.checked)}
        />
        수정
      </label>
      <label>
        <input
          type="checkbox"
          checked={!!writer.canDelete}
          disabled={permissionLoading}
          onChange={(e) => changePermission(writer, 'canDelete', e.target.checked)}
        />
        삭제
      </label>
    </span>
  );

  const saving = saveLoading || updateLoading;

  return (
    <>
      <div className="add_store_wrapper notice_board">
        <div className="notice_tabs" role="tablist">
          <button
            type="button"
            className={tab === 'list' ? 'active' : ''}
            onClick={() => setTab('list')}
          >
            공지사항
          </button>
          {access?.canGrant && (
            <button
              type="button"
              className={tab === 'grant' ? 'active' : ''}
              onClick={() => setTab('grant')}
            >
              권한관리
            </button>
          )}
        </div>

        {tab === 'list' && (
          <>
            <section className="notice_list">
              <div className="list_head">
                <div className="section_title">공지사항 목록</div>
                {access?.canCreate && (
                  <Button type="button" className="add_notice_btn" onClick={openCreateForm}>
                    공지 추가
                  </Button>
                )}
              </div>
              {noticesLoading && <p className="empty_text">불러오는 중</p>}
              {!noticesLoading && notices.length === 0 && (
                <p className="empty_text">등록된 공지사항이 없습니다.</p>
              )}
              {notices.map((notice) => (
                <article
                  className={`notice_item ${editingId === notice.popupId ? 'selected' : ''}`}
                  key={notice.popupId}
                >
                  <button
                    type="button"
                    className="notice_main"
                    onClick={() => access?.canUpdate && selectNotice(notice)}
                  >
                    <strong>{notice.title}</strong>
                    <span>{notice.createdDate}</span>
                    <em className={notice.showPopup ? 'on' : 'off'}>
                      {notice.showPopup ? '팝업 등록' : '팝업 미등록'}
                    </em>
                  </button>
                  {access?.canDelete && (
                    <Button
                      type="button"
                      className="delete_btn"
                      disabled={deleteLoading}
                      onClick={() => confirmDeleteBtn(notice.popupId)}
                    >
                      삭제
                    </Button>
                  )}
                </article>
              ))}
            </section>

            {showForm && (
              <section className="notice_form">
                <div className="section_title">{editingId == null ? '공지 추가' : '공지 수정'}</div>
                <div className="delete_btn_wrapper">
                  <Button
                    type="button"
                    onClick={() => setPreview(true)}
                    disabled={title.trim().length === 0 || isEmptyNotice(content)}
                    className="delete_btn preview_btn"
                  >
                    미리보기
                  </Button>
                  <Button type="button" className="delete_btn preview_btn" onClick={resetForm}>
                    닫기
                  </Button>
                </div>
                <Form.Control
                  value={title}
                  type="text"
                  maxLength={80}
                  className="input_section"
                  placeholder="공지사항 제목"
                  onChange={(e) => setTitle(e.target.value)}
                />
                <NoticeRichTextEditor
                  key={formKey}
                  initialContent={content}
                  onChange={setContent}
                  onUploadImage={uploadNoticeImage}
                />
                <Form.Check
                  className="popup_check"
                  type="switch"
                  id="notice-popup-switch"
                  label="팝업으로 등록"
                  checked={showPopup}
                  onChange={(e) => setShowPopup(e.target.checked)}
                />
                <p className="section_desc">
                  {showPopup
                    ? '저장하면 로그인 후 팝업으로 표시됩니다.'
                    : '저장하면 목록에만 남고 팝업으로는 뜨지 않습니다.'}
                </p>
                <Button className="confirm_btn" onClick={saveNotificationBtn} disabled={saving}>
                  {saving ? '저장중' : '저장하기'}
                </Button>
              </section>
            )}
          </>
        )}

        {tab === 'grant' && access?.canGrant && (
          <section className="writer_box">
            <div className="section_title">작성 권한</div>
            <p className="section_desc">
              계정별 공지사항 등록, 수정, 삭제 권한을 켜고 끌 수 있습니다.
            </p>
            <div className="grant_row">
              <Form.Control
                value={grantId}
                placeholder="권한을 줄 로그인 아이디"
                onChange={(e) => setGrantId(e.target.value)}
              />
              <Button type="button" onClick={grantBtn} disabled={grantLoading}>
                {grantLoading ? '추가중' : '추가'}
              </Button>
            </div>
            <div className="writer_table">
              <div className="writer_row head">
                <span>아이디</span>
                <span>이름</span>
                <span>구분</span>
                <span>권한</span>
                <span />
              </div>
              {(writers?.masters ?? []).map((writer) => (
                <div className="writer_row" key={`m-${writer.userId}`}>
                  <span>{writer.loginId}</span>
                  <span>{writer.name}</span>
                  <span>마스터</span>
                  {permissionCell(writer)}
                  <span />
                </div>
              ))}
              {(writers?.granted ?? []).map((writer) => (
                <div className="writer_row" key={`g-${writer.userId}`}>
                  <span>{writer.loginId}</span>
                  <span>{writer.name}</span>
                  <span>부여</span>
                  {permissionCell(writer)}
                  <span className="row_actions">
                    <button
                      type="button"
                      className="danger"
                      onClick={() => revokeWriter(writer)}
                      disabled={revokeLoading}
                    >
                      삭제
                    </button>
                  </span>
                </div>
              ))}
              {(writers?.granted ?? []).length === 0 && (
                <p className="empty_text">추가로 부여한 작성 권한이 없습니다.</p>
              )}
            </div>
          </section>
        )}
      </div>
      <NotificationModal
        notificationData={{
          popupId: 0,
          title,
          content,
          active: showPopup,
        }}
        show={preview}
        onHide={() => setPreview(false)}
      />
    </>
  );
};

export default NotificationManagementContainer;
